import { callTaskEventIdSchema } from "@repo/app-types/entities/callTaskEvent";
import { asRecordingId } from "@repo/app-types/entities/recording";
import type {
  AccountGradingRecording,
  CustomerInteractionTextThread,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import isEqual from "lodash-es/isEqual.js";
import { useCallback, useEffect, useState, type RefObject } from "react";
import {
  findElementAtReadingLine,
  scrollToTranscriptElement,
} from "../../components/transcript/scroll.js";
import { findFragmentIndexAtTime } from "./gradingHelpers.js";
import type { TranscriptPosition } from "./transcriptNavigationHelpers.js";

interface UseTranscriptOutlineSyncOptions {
  transcriptContainerRef: RefObject<HTMLElement | null>;
  sortedRecordings: readonly AccountGradingRecording[];
  textThread?: CustomerInteractionTextThread;
}

export function useTranscriptOutlineSync(
  options: UseTranscriptOutlineSyncOptions,
) {
  const { transcriptContainerRef, sortedRecordings, textThread } = options;

  const [readingPosition, setReadingPosition] = useState<TranscriptPosition>();

  // Scrolls the transcript to the given position. Callers that move audio seek themselves.
  const scrollToTranscriptPosition = useCallback(
    (position: TranscriptPosition): void => {
      const transcriptContainer = transcriptContainerRef.current;
      if (!transcriptContainer) return;

      if (position.type === "textMessage") {
        const text = transcriptContainer.querySelector(
          `[data-text-event-id="${CSS.escape(position.textMessageId)}"]`,
        );
        // Texts leave the playhead where it is, so audio following treats this like the reader's own scroll
        if (text) {
          scrollToTranscriptElement(
            transcriptContainer,
            text,
            "smooth",
            "reader",
          );
        }
        return;
      }

      const recording = sortedRecordings.find(
        (rec) => rec.id === position.recordingId,
      );
      const fragments = recording?.transcript?.fragments;
      if (!fragments) return;
      const fragmentIndex =
        position.fragmentIndex ??
        findFragmentIndexAtTime(fragments, position.timestamp);
      if (!fragments[fragmentIndex]) return;

      const element = transcriptContainer.querySelector(
        `[data-recording-id="${CSS.escape(position.recordingId)}"][data-fragment-index="${fragmentIndex}"]`,
      );
      if (element) scrollToTranscriptElement(transcriptContainer, element);
    },
    [transcriptContainerRef, sortedRecordings],
  );

  // Tracks the item on the reading line as the transcript scrolls, and again when recordings or texts load
  const recordingCount = sortedRecordings.length;
  useEffect(() => {
    const transcriptContainer = transcriptContainerRef.current;
    if (!transcriptContainer) return;

    let frame: number | undefined;

    const updateReadingPosition = () => {
      frame = undefined;
      // Queried on each update because transcript rows re-render, as when tool activity splits a fragment group.
      // Indexed texts also carry their copy's fragment attributes; they read as texts.
      const elements = Array.from(
        transcriptContainer.querySelectorAll(
          "[data-recording-id][data-timestamp], [data-text-event-id]",
        ),
      );
      const element = findElementAtReadingLine(transcriptContainer, elements);
      const position = element && readTranscriptPosition(element);
      if (!position) return;
      setReadingPosition((previous) =>
        isEqual(previous, position) ? previous : position,
      );
    };
    const handleScroll = () => {
      frame ??= requestAnimationFrame(updateReadingPosition);
    };

    transcriptContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    updateReadingPosition();

    return () => {
      transcriptContainer.removeEventListener("scroll", handleScroll);
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [recordingCount, textThread, transcriptContainerRef]);

  return {
    readingPosition,
    scrollToTranscriptPosition,
  } as const;
}

function readTranscriptPosition(
  element: Element,
): TranscriptPosition | undefined {
  const textMessageId = element.getAttribute("data-text-event-id");
  if (textMessageId) {
    return {
      type: "textMessage",
      textMessageId: callTaskEventIdSchema.parse(textMessageId),
    };
  }
  const recordingId = element.getAttribute("data-recording-id");
  const timestamp = Number(element.getAttribute("data-timestamp"));
  if (!recordingId || !Number.isFinite(timestamp)) return undefined;
  return { type: "call", recordingId: asRecordingId(recordingId), timestamp };
}
